import type { OptionDB } from "@project/emerge";
import { Sides } from "@project/emerge-types";
import { Box3 } from "three";
import { InteriorType } from "../enum/InteriorType";
import { LTCaseUserOption } from "../enum/LTCaseUserOption";
import { PaddingStyle } from "../enum/PaddingStyle";
import { createSingleItemTree } from "../genetic_optimize/ContentTree";
import type {
  AccessoryCompartmentOptions,
  CaseInteriorOptions,
} from "../interfaces/AccessoryCompartmentOptions";
import type { CaseCompartment } from "../interfaces/CaseCompartment";
import { createNewCompartment } from "../structure/Compartment";

export type PaddingMap = { [side in Sides]: number };
export type PaddingStyleMap = { [side in Sides]: PaddingStyle };

export function createEqualPaddingMap(padding: number): PaddingMap {
  return {
    [Sides.TOP]: padding,
    [Sides.FRONT]: padding,
    [Sides.BACK]: padding,
    [Sides.BOTTOM]: padding,
    [Sides.LEFT]: padding,
    [Sides.RIGHT]: padding,
  };
}

export function createEqualPaddingStyleMap(
  style: PaddingStyle = PaddingStyle.STANDARD,
): PaddingStyleMap {
  return {
    [Sides.TOP]: style,
    [Sides.FRONT]: style,
    [Sides.BACK]: style,
    [Sides.BOTTOM]: style,
    [Sides.LEFT]: style,
    [Sides.RIGHT]: style,
  };
}

export function resolvePaddingStyleMap(
  map: PaddingStyleMap | undefined,
  fallback: PaddingStyle = PaddingStyle.STANDARD,
): PaddingStyleMap {
  if (!map) {
    return createEqualPaddingStyleMap(fallback);
  }
  return {
    [Sides.TOP]: map[Sides.TOP] ?? fallback,
    [Sides.FRONT]: map[Sides.FRONT] ?? fallback,
    [Sides.BACK]: map[Sides.BACK] ?? fallback,
    [Sides.BOTTOM]: map[Sides.BOTTOM] ?? fallback,
    [Sides.LEFT]: map[Sides.LEFT] ?? fallback,
    [Sides.RIGHT]: map[Sides.RIGHT] ?? fallback,
  };
}

// const sizeWithPadding = size.add(new Vector3(
//     padding[Sides.LEFT] + padding[Sides.RIGHT] + tolerance[0],
//     padding[Sides.BOTTOM] + tolerance[1],
//     padding[Sides.FRONT] + padding[Sides.BACK] + tolerance[2],
// ));
// boxWithPadding: new Box3(
//     sizeWithPadding.clone().multiplyScalar(-0.5),
//     sizeWithPadding.clone().multiplyScalar(.5),
// ),

export function calculateBoxWithPadding(compartment: CaseCompartment): Box3 {
  const { center, sizeWithPadding } = compartment;
  return new Box3().setFromCenterAndSize(center, sizeWithPadding.clone());
}

export function createAccessoryCompartment(
  optionDb: OptionDB,
  side: Sides.FRONT | Sides.LEFT | Sides.RIGHT | Sides.BACK = Sides.RIGHT,
  size = 250,
  padding = "nopad",
): AccessoryCompartmentOptions {
  return {
    padding: optionDb.getByName(LTCaseUserOption.PaddingType, padding),
    side,
    size,
  };
}

export function createCaseInterior(optionDb: OptionDB): CaseInteriorOptions {
  const item = createNewCompartment();
  return {
    tolerance: { x: 7, y: 2, z: 7 },
    trayMaterial: optionDb.getByName(
      LTCaseUserOption.TrayMaterialType,
      "PLW1415",
    ),
    tree: createSingleItemTree(item),
    contents: [item],
    type: InteriorType.CASE,
    upperPaddingAlignment: true,
  };
}
